import ErrorWithData from "@/utils/ErrorWithData"

export const API_URL_BASE = import.meta.env.VITE_PUBLIC_API_BASE_URL || "http://localhost:9000"
export const AUTH_COOKIE_PATH = "Authorization"

type ErrorMessage = Record<string, string>
type Message = Record<string, any>
type GenericHeaders = Record<string, string>

export async function processResponse<T = Message>(r: Response) {
  const response = r
  let data: ErrorMessage | Message | string | undefined
  if (response.status === 204) {
    return {
      data: null as T,
      response,
    }
  }
  try {
    data = await r.text()
    data = JSON.parse(data)
  } catch (e) {
    console.warn("bad JSON response", data, e)
  }
  if (!response.ok) {
    throw new ErrorWithData(
      response?.statusText || "Unknown error",
      typeof data === "object"
        ? { ...(data as { [k: string]: any }), response, url: response.url }
        : { url: response.url, response }
    )
  }
  return {
    data,
    response,
  } as {
    data: T
    response: Response
  }
}

export function makeAPI(apiBase: string) {
  return {
    headers: {} as GenericHeaders,
    token: null as string | null,
    endpoint(route: string) {
      return new URL(route, apiBase)
    },
    async get<T = Message>(route: string, options: RequestInit = {}) {
      return fetch(this.endpoint(route), {
        ...options,
        headers: this.headers,
      }).then(processResponse<T>)
    },
    async modify<T = Message>(
      // eslint-disable-next-line default-param-last
      method = "POST",
      route: string,
      data: { [propName: string]: any } | FormData = {},
      _options: RequestInit = {}
    ) {
      if (typeof window !== "undefined" && data instanceof FormData) {
        delete this.headers["Content-Type"]
      } else {
        this.headers["Content-Type"] = "application/json"
      }
      const options = { ..._options }
      if (method !== "GET" && method !== "DELETE") {
        options.body = data instanceof FormData ? data : JSON.stringify(data)
      }
      return fetch(this.endpoint(route), {
        method,
        ...options,
        headers: {
          ...(options.headers || {}),
          ...this.headers,
        },
      }).then(processResponse<T>)
    },
    async post<T = Message>(route: string, data: { [propName: string]: any } = {}, options: RequestInit = {}) {
      return this.modify<T>("POST", route, data, options)
    },
    async put<T = Message>(route: string, data?: { [propName: string]: any }, options: RequestInit = {}) {
      return this.modify<T>("PUT", route, data, options)
    },
    async patch<T = Message>(route: string, data: { [propName: string]: any }, options: RequestInit = {}) {
      return this.modify<T>("PATCH", route, data, options)
    },
    async delete(route: string, data: { [propName: string]: any } = {}, options: RequestInit = {}) {
      return this.modify("DELETE", route, data, options)
    },
    setToken(key?: string) {
      if (!key) {
        return this
      }
      this.token = key
      this.headers.Authorization = `Bearer ${key}`
      return this
    },
    stripToken() {
      this.token = null
      delete this.headers.Authorization
      return this
    },
  }
}

/**
 * Fetch and send data to / from cluster APIs
 * Provides a simple mechanism for using different APIs for different environments
 * Auth + standardised error handling can easily be implemented across the codebase
 *
 * @see https://bitbucket.org/josephmark/oxamii-portal/src/main/lib/api.ts#lines-88
 */
export const clusterAPI = makeAPI(import.meta.env.VITE_PUBLIC_SO_CLUSTER_URI!)
